import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import clsx from 'clsx';
import { ensureArray, safeMap, ensureString, ensureNumber } from 'utils/safeArray';

export function QuadrantChart({
  data,
  className,
  xAxisLabel = 'Impact',
  yAxisLabel = 'Ease',
  title,
  xMin = 1,
  xMax = 5,
  yMin = 1,
  yMax = 5,
  xThreshold = 4,
  yThreshold = 3,
  accentColor = '#C6161A',
  ...props
}) {
  const [activeClusterId, setActiveClusterId] = useState(null);
  const [highlightedItemId, setHighlightedItemId] = useState(null);

  const getQuadrantClassification = (impact, ease) => {
    const safeImpact = ensureNumber(impact, 0);
    const safeEase = ensureNumber(ease, 0);
    const isHighValue = safeImpact >= xThreshold;
    const isHighEase = safeEase >= yThreshold;
    const isLowValue = !isHighValue;
    const isLowEase = !isHighEase;

    if (isHighValue && isHighEase) {
      return {
        category: 'No-Brainer',
        color: 'bg-green-100 text-green-800 border-green-200',
        action: 'Execute immediately',
        description: 'High impact and easy to implement. Approve now and deliver in ≤90 days.',
      };
    }

    if (isHighValue && isLowEase) {
      return {
        category: 'Big Bet',
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        action: 'Run PoC first',
        description: 'High impact but complex. Run a 4–6 week PoC with clear success criteria.',
      };
    }

    if (isLowValue && isHighEase) {
      return {
        category: 'Quick Win',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        action: 'Bundle or defer',
        description: 'Easy to implement but limited impact. Bundle with other initiatives or defer.',
      };
    }

    return {
      category: 'Time Sink',
      color: 'bg-red-100 text-red-800 border-red-200',
      action: 'Avoid',
      description: 'Low impact and difficult. Stop unless regulatory or existential.',
    };
  };

  const bubbleColor = '#0284C7';
  const bubbleColorActive = accentColor || '#0EA5E9';
  const bubbleHalo = '#38BDF8';

  const formatScore = (score) => {
    const safeScore = ensureNumber(score, 0);
    return Number.isInteger(safeScore) ? String(safeScore) : safeScore.toFixed(1);
  };

  const toAlphabetLabel = (index) => {
    let value = index + 1;
    let label = '';

    while (value > 0) {
      const remainder = (value - 1) % 26;
      label = String.fromCharCode(65 + remainder) + label;
      value = Math.floor((value - 1) / 26);
    }

    return label;
  };

  const { chartPoints, sidebarItems } = useMemo(() => {
    const rawItems = ensureArray(data);

    const flattened = [];

    rawItems.forEach((item, index) => {
      const safeItem = item && typeof item === 'object' ? item : {};
      const baseValue = ensureNumber(safeItem.valueScore ?? safeItem.x, ensureNumber(safeItem.x, 0));
      const baseEffort = ensureNumber(safeItem.effortScore ?? safeItem.y, ensureNumber(safeItem.y, 0));
      const clusterInitiatives = ensureArray(safeItem.initiatives);

      if (clusterInitiatives.length) {
        clusterInitiatives.forEach((init, innerIndex) => {
          const safeInit = init && typeof init === 'object' ? init : {};
          flattened.push({
            sourceId: ensureString(safeInit.id ?? safeInit._id ?? `initiative-${index}-${innerIndex}`),
            title: ensureString(safeInit.label ?? safeInit.name ?? safeItem.name ?? `Initiative ${index + 1}`),
            rationale: ensureString(safeInit.rationale ?? safeItem.prioritizationRationale ?? ''),
            valueScore: ensureNumber(safeInit.valueScore ?? safeInit.x, baseValue),
            effortScore: ensureNumber(safeInit.effortScore ?? safeInit.y, baseEffort),
          });
        });
      } else {
        flattened.push({
          sourceId: ensureString(safeItem.id ?? safeItem._id ?? `initiative-${index}`),
          title: ensureString(safeItem.name, `Initiative ${index + 1}`),
          rationale: ensureString(safeItem.prioritizationRationale ?? ''),
          valueScore: baseValue,
          effortScore: baseEffort,
        });
      }
    });

    const sorted = flattened
      .map((item) => ({ ...item }))
      .sort((a, b) => {
        const scoreA = (a.valueScore || 0) + (a.effortScore || 0);
        const scoreB = (b.valueScore || 0) + (b.effortScore || 0);
        if (scoreB !== scoreA) return scoreB - scoreA;
        if (b.valueScore !== a.valueScore) return ensureNumber(b.valueScore, 0) - ensureNumber(a.valueScore, 0);
        if (b.effortScore !== a.effortScore) return ensureNumber(b.effortScore, 0) - ensureNumber(a.effortScore, 0);
        return ensureString(a.title).localeCompare(ensureString(b.title));
      });

    const clusters = new Map();

    sorted.forEach((item, idx) => {
      const valueScore = ensureNumber(item.valueScore, 0);
      const effortScore = ensureNumber(item.effortScore, 0);
      const key = `${valueScore}-${effortScore}`;
      const clusterId = `cluster-${key.replace(/\./g, '_')}`;

      if (!clusters.has(key)) {
        clusters.set(key, {
          id: clusterId,
          x: valueScore,
          y: effortScore,
          initiatives: [],
        });
      }

      const cluster = clusters.get(key);
      const alphaLabel = toAlphabetLabel(idx);
      const initiativeEntry = {
        ...item,
        rank: idx + 1,
        alphaLabel,
        clusterId,
      };
      cluster.initiatives.push(initiativeEntry);
    });

    const chartPoints = [];
    const sidebarItems = [];

    clusters.forEach((cluster) => {
      const { initiatives } = cluster;
      const primary = initiatives[0];
      chartPoints.push({
        id: cluster.id,
        clusterId: cluster.id,
        x: cluster.x,
        y: cluster.y,
        name: primary?.title || '',
        valueScore: cluster.x,
        effortScore: cluster.y,
        count: initiatives.length,
        initiatives,
      });

      initiatives.forEach((initiative) => {
        sidebarItems.push(initiative);
      });
    });

    sidebarItems.sort((a, b) => a.rank - b.rank);

    return { chartPoints, sidebarItems };
  }, [data]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) {
      return null;
    }

    const point = payload[0].payload || {};
    const classification = getQuadrantClassification(point.x, point.y);
    const initiatives = ensureArray(point.initiatives);
    const hasMany = initiatives.length > 1;
    const titleContent = hasMany
      ? `${initiatives.length} initiatives at this score`
      : ensureString(initiatives[0]?.title || point.name);

    const buildScoreBadge = (label, value) => (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-3 py-1 text-xs font-semibold text-zinc-600">
        {label}
        <span className="text-zinc-900 text-sm font-bold">{`${formatScore(value)}/5`}</span>
      </span>
    );

    return (
      <div className="bg-white border border-zinc-200 rounded-xl shadow-lg p-4 max-w-sm space-y-3 text-left">
        <div className={clsx('flex items-start gap-3', hasMany ? 'justify-between' : 'justify-start')}>
          <p className="text-[12px] font-semibold text-zinc-900 leading-snug line-clamp-2">{titleContent}</p>
          <span className={`px-3 py-[5px] rounded-lg text-[11px] font-semibold uppercase tracking-wide border whitespace-nowrap ${classification.color}`}>
            {classification.category}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-left">
          {buildScoreBadge(xAxisLabel, ensureNumber(point.x, 0))}
          {buildScoreBadge(yAxisLabel, ensureNumber(point.y, 0))}
        </div>

        {hasMany ? (
          <div className="border-t border-zinc-100 pt-3 space-y-2">
            {initiatives.map((initiative, idx) => (
              <div key={`${point.clusterId || point.id || 'initiative'}-${idx}`} className="space-y-1 text-[12px] text-zinc-600">
                <p className="font-semibold text-zinc-700 leading-snug text-sm">
                  {ensureString(initiative.alphaLabel)}. {ensureString(initiative.title)}
                </p>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const CustomScatterPoint = ({ cx, cy, payload }) => {
    const safePayload = payload && typeof payload === 'object' ? payload : {};
    const clusterId = ensureString(safePayload.clusterId || safePayload.id, '');
    const clusterCount = ensureNumber(safePayload.count, 1);
    const isActive = activeClusterId === clusterId;
    const badgeContent = String(Math.max(clusterCount, 1));
    const baseRadius = clusterCount > 1 ? 16 : 14;

    return (
      <g
        className="cursor-pointer transition-transform"
        onMouseEnter={() => {
          setActiveClusterId(clusterId);
          setHighlightedItemId(null);
        }}
        onMouseLeave={() => {
          setActiveClusterId(null);
          setHighlightedItemId(null);
        }}
      >
        <circle
          cx={cx}
          cy={cy}
          r={isActive ? baseRadius + 2 : baseRadius}
          fill={isActive ? bubbleColorActive : bubbleColor}
          stroke={isActive ? bubbleHalo : '#bae6fd'}
          strokeWidth={isActive ? 3 : 2.2}
          className="transition-all duration-200"
        />
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#FFFFFF"
          fontSize={isActive ? 13 : 12}
          fontWeight="bold"
          pointerEvents="none"
        >
          {badgeContent}
        </text>
      </g>
    );
  };

  return (
    <div className={clsx('w-full mx-auto relative flex flex-col gap-6', className)} {...props}>
      {title && (
        <h3 className="text-base md:text-lg font-semibold text-zinc-900 text-center">
          {title}
        </h3>
      )}

      <div className="grid gap-5 grid-cols-1 xl:grid-cols-[2fr_1fr] w-full">
        <div className="flex flex-col gap-4">
          <div className="rounded-3xl bg-white shadow-sm">
            <div className="h-[280px] sm:h-[360px] md:h-[420px] xl:h-[460px]">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                  margin={{ top: 20, right: 28, bottom: 48, left: 48 }}
                  data={chartPoints}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#CBD5F5" strokeWidth={0.8} />

                  <ReferenceLine x={xThreshold} stroke="#71717A" strokeDasharray="3 3" />
                  <ReferenceLine y={yThreshold} stroke="#71717A" strokeDasharray="3 3" />

                  <ReferenceArea x1={xThreshold} x2={xMax} y1={yThreshold} y2={yMax} fill="#10B981" fillOpacity={0.15} />
                  <ReferenceArea x1={xMin} x2={xThreshold} y1={yThreshold} y2={yMax} fill="#F59E0B" fillOpacity={0.15} />
                  <ReferenceArea x1={xMin} x2={xThreshold} y1={yMin} y2={yThreshold} fill="#EF4444" fillOpacity={0.15} />
                  <ReferenceArea x1={xThreshold} x2={xMax} y1={yMin} y2={yThreshold} fill="#3B82F6" fillOpacity={0.15} />

                  <XAxis
                    type="number"
                    dataKey="x"
                    name={xAxisLabel}
                    domain={[xMin, xMax]}
                    tick={{ fontSize: 12, fill: '#334155', fontWeight: 600 }}
                    axisLine={{ stroke: '#71717A' }}
                    tickLine={{ stroke: '#71717A' }}
                    ticks={[1, 2, 3, 4, 5]}
                    label={{
                      value: xAxisLabel,
                      position: 'bottom',
                      offset: 14,
                      style: { fontSize: 16, fontWeight: 700, fill: '#0f172a', letterSpacing: '0.02em' },
                    }}
                  />

                  <YAxis
                    type="number"
                    dataKey="y"
                    name={yAxisLabel}
                    domain={[yMin, yMax]}
                    tick={{ fontSize: 12, fill: '#334155', fontWeight: 600 }}
                    axisLine={{ stroke: '#71717A' }}
                    tickLine={{ stroke: '#71717A' }}
                    ticks={[1, 2, 3, 4, 5]}
                    label={{
                      value: yAxisLabel,
                      angle: -90,
                      position: 'insideLeft',
                      offset: -28,
                      style: { fontSize: 16, fontWeight: 700, fill: '#0f172a', letterSpacing: '0.02em' },
                    }}
                  />

                  <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 30 }} />

                  <Scatter
                    dataKey="y"
                    fill={bubbleColor}
                    shape={<CustomScatterPoint />}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-2xl bg-white px-3.5 py-2.5 shadow-sm">
            <div className="flex flex-wrap items-center justify-center gap-x-2 md:gap-x-3.5 gap-y-1.5 md:gap-y-2 text-[10px] text-zinc-600 font-medium">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-sm bg-green-500 shadow-sm" />
                <span className="font-semibold text-[11px] md:text-[13px] text-zinc-700">No-Brainers</span>
                <span className="text-[10px] md:text-[11px] text-zinc-500 hidden sm:inline">(Execute immediately)</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-sm bg-blue-500 shadow-sm" />
                <span className="font-semibold text-[11px] md:text-[13px] text-zinc-700">Big Bets</span>
                <span className="text-[10px] md:text-[11px] text-zinc-500 hidden sm:inline">(Run PoC first)</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-sm bg-yellow-500 shadow-sm" />
                <span className="font-semibold text-[11px] md:text-[13px] text-zinc-700">Quick Wins</span>
                <span className="text-[10px] md:text-[11px] text-zinc-500 hidden sm:inline">(Bundle or defer)</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 md:w-2.5 md:h-2.5 rounded-sm bg-red-500 shadow-sm" />
                <span className="font-semibold text-[11px] md:text-[13px] text-zinc-700">Time Sinks</span>
                <span className="text-[10px] md:text-[11px] text-zinc-500 hidden sm:inline">(Avoid)</span>
              </div>
            </div>
          </div>
        </div>

        <aside className="rounded-2xl bg-white flex flex-col shadow-sm">
          <div className="flex-1 space-y-3.5 py-3 pr-1 pl-3">
            {sidebarItems.length === 0 ? (
              <p className="text-xs text-zinc-500 italic">No initiatives to display.</p>
            ) : (
              safeMap(sidebarItems, (item) => {
                const safeItem = item && typeof item === 'object' ? item : {};
                return (
                  <button
                    key={ensureString(safeItem.sourceId) || safeItem.alphaLabel}
                    type="button"
                    className="w-full text-left flex items-start gap-3.5 border border-transparent rounded-2xl focus:outline-none focus:ring-1 focus:ring-sky-200 px-2 py-2.5 hover:bg-slate-50 transition-colors"
                    onMouseEnter={() => {
                      setActiveClusterId(safeItem.clusterId);
                      setHighlightedItemId(ensureString(safeItem.sourceId) || ensureString(safeItem.alphaLabel));
                    }}
                    onMouseLeave={() => {
                      setActiveClusterId(null);
                      setHighlightedItemId(null);
                    }}
                    onFocus={() => {
                      setActiveClusterId(safeItem.clusterId);
                      setHighlightedItemId(ensureString(safeItem.sourceId) || ensureString(safeItem.alphaLabel));
                    }}
                    onBlur={() => {
                      setActiveClusterId(null);
                      setHighlightedItemId(null);
                    }}
                  >
                    <span
                      className={clsx(
                        'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold shadow-sm mt-1 transition-colors duration-150',
                        (highlightedItemId
                          ? highlightedItemId === (ensureString(safeItem.sourceId) || ensureString(safeItem.alphaLabel))
                          : activeClusterId === safeItem.clusterId)
                          ? 'bg-rose-500 text-white'
                          : 'bg-sky-500 text-white'
                      )}
                    >
                      {ensureString(safeItem.alphaLabel)}
                    </span>
                    <div className="flex-1 space-y-2">
                      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                        <span className="block text-sm md:text-base font-semibold text-zinc-900 leading-snug">
                          {ensureString(safeItem.title)}
                        </span>
                        <div className="flex flex-col items-end text-[11px] font-semibold gap-1">
                          <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-[3px] text-[11px] text-sky-700 border border-sky-200/80 shadow-sm">
                            <span>Impact</span>
                            <span className="text-sky-900 text-sm font-bold leading-none">{formatScore(safeItem.valueScore)}</span>
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-[3px] text-[11px] text-emerald-700 border border-emerald-200/80 shadow-sm">
                            <span>Ease</span>
                            <span className="text-emerald-900 text-sm font-bold leading-none">{formatScore(safeItem.effortScore)}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

QuadrantChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ).isRequired,
  className: PropTypes.string,
  xAxisLabel: PropTypes.string,
  yAxisLabel: PropTypes.string,
  title: PropTypes.string,
  xMin: PropTypes.number,
  xMax: PropTypes.number,
  yMin: PropTypes.number,
  yMax: PropTypes.number,
  xThreshold: PropTypes.number,
  yThreshold: PropTypes.number,
  accentColor: PropTypes.string,
};
